import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { useDebounce } from '@fuse/hooks';
import FuseUtils from '@fuse/utils';
import { Divider, Typography } from '@material-ui/core';
import Grow from '@material-ui/core/Grow';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import authService from 'app/services/authService';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext/GlobalContext';
import clsx from 'clsx';
import React, { useContext, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Manager, Popper, Reference } from 'react-popper';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FuseNavItem from '../FuseNavItem';

const useStyles = makeStyles((theme) => ({
  cadastro: {
    marginBottom: 16,
  },
  gestao: {
    marginBottom: 16,
  },
  paper: {
    backgroundColor: `${theme.palette.primary.main}!important`,
    '&.MuiPaper-root': {
      backgroundColor: `${theme.palette.primary.main}!important`,
    },
  },
  root: {
    color: theme.palette.text.primary,
    '&.active, &.active:hover, &.active:focus': {
      backgroundColor: `${theme.palette.primary.main}!important`,
      color: `${theme.palette.primary.contrastText}!important`,
      '& .list-item-text-primary': {
        color: 'inherit',
      },
      '& .list-item-icon': {
        color: 'inherit',
      },
    },
    '& .list-item-text': {
      padding: '0 0 0 16px',
    },
    '&.level-0': {
      height: 44,
      borderRadius: 4,
      '&:hover': {
        background: 'transparent',
      },
    },
  },
  children: {},
  popper: {
    zIndex: 999,
  },
  popperClose: {
    pointerEvents: 'none',
  },
}));

function FuseNavHorizontalGroup(props) {
  const userRole = useSelector(({ auth }) => auth.user.role);

  const classes = useStyles(props);
  const [opened, setOpened] = useState(false);
  const { item, nestedLevel, dense } = props;
  const theme = useTheme();
  const { t } = useTranslation('navigation');
  const [, setModalRelatorios] = useContext(GlobalContext).modalRelatorios;
  const [, setModal] = useContext(GlobalContext).modal;
  const dispatch = useDispatch();

  const FuseNavItemModais = (_item) => {
    return (
      <div
        onClick={(_) => {
          if (userRole.includes(_item.authority)) {
            if (_item?.financeiro) {
              setModal(_item?.financeiro);
            } else if (_item?.relatorio) {
              setModalRelatorios(_item?.relatorio);
            }
          }
        }}
      >
        <FuseNavItem
          key={_item.id}
          type={`horizontal-${_item.type}`}
          item={_item}
          nestedLevel={nestedLevel}
          dense={dense}
        />
      </div>
    );
  };

  const handleToggle = useDebounce((open) => {
    setOpened(open);
  }, 150);

  if (!FuseUtils.hasPermission(item.auth, userRole)) {
    return null;
  }

  function isUrlInChildren(parent, url) {
    if (!parent.children) {
      return false;
    }

    for (let i = 0; i < parent.children.length; i += 1) {
      if (parent.children[i].children) {
        if (isUrlInChildren(parent.children[i], url)) {
          return true;
        }
      }

      if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
        return true;
      }
    }

    return false;
  }

  const handleClickRelatorios = (event, item) => {
    if (item.id === 'relatorios') {
      let possuiPermissão = item.authority.some((auth) => authService.hasAuthority(auth));

      if (!possuiPermissão) {
        event.preventDefault();
        dispatch(
          Actions.showMessage({
            message: 'Você não possui permissão para acessar esta tela.',
            variant: 'error',
          })
        );
      }
    }
  };

  const CustomIcon = item.icon;

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div ref={ref}>
            <ListItem
              button
              className={clsx(
                'list-item ',
                classes.root,
                'relative',
                `level-${nestedLevel}`,
                isUrlInChildren(item, props.location.pathname) && 'active'
              )}
              onMouseEnter={() => handleToggle(true)}
              onMouseLeave={() => handleToggle(false)}
              aria-owns={opened ? 'menu-list-grow' : null}
              aria-haspopup="true"
              component={item.url ? NavLinkAdapter : 'li'}
              to={item.url}
              role="button"
              onClick={(ev) => handleClickRelatorios(ev, item)}
            >
              {item.icon && typeof item.icon === 'string' ? (
                <Icon className="list-item-icon text-16 flex-shrink-0" color="action">
                  {item.icon}
                </Icon>
              ) : (
                <CustomIcon size={14} color="#fff" />
              )}
              <ListItemText
                className="list-item-text"
                primary={item.translate ? t(item.translate) : item.title}
                classes={{ primary: 'text-14' }}
              />
              {nestedLevel > 0 && (
                <IconButton disableRipple className="w-16 h-16 ltr:ml-4 rtl:mr-4 p-0" color="inherit">
                  <Icon className="text-16 arrow-icon">
                    {theme.direction === 'ltr' ? 'keyboard_arrow_right' : 'keyboard_arrow_left'}
                  </Icon>
                </IconButton>
              )}
            </ListItem>
          </div>
        )}
      </Reference>
      {!!(item.gestao?.length || !!item.cadastro?.length) &&
        ReactDOM.createPortal(
          <Popper
            placement={
              nestedLevel === 0
                ? theme.direction === 'ltr'
                  ? 'center'
                  : 'bottom-end'
                : theme.direction === 'ltr'
                ? 'right'
                : 'left'
            }
            eventsEnabled={opened}
            positionFixed
          >
            {({ ref, style, placement, arrowProps }) =>
              opened && (
                <div
                  ref={ref}
                  style={{
                    ...style,
                    zIndex: 999 + nestedLevel,
                  }}
                  data-placement={placement}
                  className={clsx(classes.popper, { [classes.popperClose]: !opened })}
                >
                  <Grow in={opened} id="menu-list-grow" style={{ transformOrigin: '0 0 0' }}>
                    <Paper
                      style={{ display: 'flex', flexDirection: 'row' }}
                      className={classes.paper}
                      onMouseEnter={() => handleToggle(true)}
                      onMouseLeave={() => handleToggle(false)}
                    >
                      {item.gestao?.length && (
                        <Paper
                          elevation={0}
                          onMouseEnter={() => handleToggle(true)}
                          onMouseLeave={() => handleToggle(false)}
                          className={classes.paper}
                        >
                          <Typography
                            align="center"
                            style={{
                              minWidth: 200,
                              marginTop: 16,
                              color:
                                process.env.REACT_APP_KEYCLOAK_CLIENT_ID === 'financeiro-front' ? '#707070' : '#FFFFFF',
                            }}
                          >
                            GESTÃO
                          </Typography>
                          {item.gestao && (
                            <ul className={clsx(classes.gestao, 'popper-navigation-list', dense && 'dense', 'px-0')}>
                              {item.gestao.map((_item) =>
                                _item?.relatorio || _item?.financeiro ? (
                                  FuseNavItemModais(_item)
                                ) : (
                                  <FuseNavItem
                                    key={_item.id}
                                    type={`horizontal-${_item.type}`}
                                    item={_item}
                                    nestedLevel={nestedLevel}
                                    dense={dense}
                                  />
                                )
                              )}
                            </ul>
                          )}
                        </Paper>
                      )}

                      <Divider orientation="vertical" style={{ height: 'auto' }} />

                      {item.cadastro?.length && (
                        <Paper
                          elevation={0}
                          onMouseEnter={() => handleToggle(true)}
                          onMouseLeave={() => handleToggle(false)}
                          className={classes.paper}
                        >
                          <Typography
                            align="center"
                            style={{
                              minWidth: 200,
                              marginTop: 16,
                              color:
                                process.env.REACT_APP_KEYCLOAK_CLIENT_ID === 'financeiro-front' ? '#707070' : '#FFFFFF',
                            }}
                          >
                            CADASTRO
                          </Typography>
                          {item.cadastro && (
                            <ul className={clsx(classes.cadastro, 'popper-navigation-list', dense && 'dense', 'px-0')}>
                              {item.cadastro.map((_item) => (
                                <FuseNavItem
                                  key={_item.id}
                                  type={`horizontal-${_item.type}`}
                                  item={_item}
                                  nestedLevel={nestedLevel}
                                  dense={dense}
                                />
                              ))}
                            </ul>
                          )}
                        </Paper>
                      )}
                    </Paper>
                  </Grow>
                </div>
              )
            }
          </Popper>,
          document.querySelector('#root')
        )}
    </Manager>
  );
}

FuseNavHorizontalGroup.defaultProps = {};

const NavHorizontalGroup = withRouter(React.memo(FuseNavHorizontalGroup));

export default NavHorizontalGroup;
